import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Icon } from "@iconify/react";
import { LUXURY } from "data/guestLuxuryTheme";

/**
 * Built-in animated icons from icon libraries:
 * - Iconify `svg-spinners` (SMIL animation in the SVG)
 * - Lordicon Lottie icons (animation baked into each JSON)
 */
const LORDICON_SRC = {
  wifi: "/assets/guest-icons/signal.json",
  housekeeping: "/assets/guest-icons/service-desk.json",
  makeup: "/assets/guest-icons/home.json",
  laundry: "/assets/guest-icons/basket.json",
  offers: "/assets/guest-icons/celebration.json",
  events: "/assets/guest-icons/confetti.json",
  help: "/assets/guest-icons/service.json",
  stay: "/assets/guest-icons/home.json",
  dine: "/assets/guest-icons/shopping.json",
  "room-service": "/assets/guest-icons/delivery.json",
};

const ICONIFY_ANIMATED = {
  wifi: "svg-spinners:wifi",
};

let lordiconReady;

const ensureLordicon = () => {
  if (typeof window === "undefined") return Promise.resolve(true);
  if (customElements.get("lord-icon")) return Promise.resolve(true);
  if (!lordiconReady) {
    lordiconReady = import("@lordicon/element")
      .then(({ defineElement }) => {
        if (!customElements.get("lord-icon")) defineElement();
        return true;
      })
      .catch(() => false);
  }
  return lordiconReady;
};

const AnimatedQuickIcon = ({ name = "stay", size = 30, color }) => {
  const tone = color || LUXURY.goldDeep;
  const iconifyName = ICONIFY_ANIMATED[name];
  const lordSrc = LORDICON_SRC[name] || LORDICON_SRC.stay;
  const [lordReady, setLordReady] = useState(
    typeof window !== "undefined" && !!customElements.get("lord-icon")
  );

  useEffect(() => {
    if (iconifyName) return undefined;
    let alive = true;
    ensureLordicon().then((ok) => {
      if (alive && ok) setLordReady(true);
    });
    return () => {
      alive = false;
    };
  }, [iconifyName]);

  if (iconifyName) {
    return (
      <Box
        aria-hidden
        sx={{
          width: size + 4,
          height: size + 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: tone,
          flexShrink: 0,
          "& svg": { display: "block" },
        }}
      >
        <Icon icon={iconifyName} width={size} height={size} color="currentColor" />
      </Box>
    );
  }

  return (
    <Box
      aria-hidden
      sx={{
        width: size + 4,
        height: size + 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        lineHeight: 0,
      }}
    >
      {lordReady
        ? React.createElement("lord-icon", {
            src: lordSrc,
            trigger: "loop",
            stroke: "light",
            colors: `primary:${tone},secondary:${LUXURY.gold}`,
            style: { width: size, height: size },
          })
        : (
          <Icon
            icon="mdi:circle-outline"
            width={size * 0.7}
            height={size * 0.7}
            color={tone}
          />
        )}
    </Box>
  );
};

export default AnimatedQuickIcon;
